import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReactPhoneInput from 'react-phone-input-material-ui';

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { newOffice } from 'utils/schema';
import { useAppDispatch } from 'store/hooks';
import { updateOffice } from 'store/slices/secureMessageSlice';
import { toast } from 'react-toastify';
import { OrganizationStatus, OrganizationTypes, Regions, USStates } from 'utils/dropDownList';
import { StyledDialog } from 'theme/index';

const UpdateOffice = (props: any) => {
  const [open, setOpen] = useState<boolean>(true);
  const [disableSaveButton, setDisableSaveButton] = useState(false);
  const dispatch = useAppDispatch();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
  } = useForm({
    resolver: yupResolver(newOffice),
    mode: 'all',
  });

  useEffect(() => {
    setPropety(props);
  }, [props.editData, setValue]);

  const handleClose = () => {
    setOpen(false);
    props?.onClose();
  };

  const setPropety = (props: any) => {
    if (props.editData !== null) {
      setValue('id', props?.editData[0]?.id);
      setValue('officeName', props?.editData[0]?.officeName);
      setValue('officeType', props?.editData[0]?.officeType);
      setValue('businessEmail', props?.editData[0]?.businessEmail);
      setValue('officeFaxNumber', props?.editData[0]?.officeFaxNumber);
      setValue('officePhoneNumber', props?.editData[0]?.officePhoneNumber);
      setValue('region', props?.editData[0]?.region);
      setValue('websiteUrl', props?.editData[0]?.websiteUrl);
      setValue('address', props?.editData[0]?.address);
      setValue('status', props?.editData[0]?.status);
      setValue('state', props?.editData[0]?.state);
      setValue('city', props?.editData[0]?.city);
      setValue('zipcode', props?.editData[0]?.zipcode);
    }
  };

  const onSubmitForm = (formData: any) => {
    setDisableSaveButton(true);
    const data = {
      id: props?.editData !== null ? props?.editData[0]?.id : null,
      officeName: formData?.officeName,
      officeType: formData?.officeType,
      businessEmail: formData?.businessEmail,
      officeFaxNumber: formData?.officeFaxNumber,
      officePhoneNumber: formData?.officePhoneNumber,
      region: formData?.region,
      websiteUrl: formData?.websiteUrl,
      status: formData?.status,
      state: formData?.state,
      city: formData?.city,
      zipcode: formData?.zipcode,
      address: formData?.address,
    };
    dispatch(updateOffice(data))
      .unwrap()
      .then((res: any) => {
        if (res.status === 200) {
          toast('Office details updated successfully', {
            type: 'success',
            icon: true,
          });
          setTimeout(() => {
            props.onClose();
            setDisableSaveButton(false);
          }, 1000);
        }
      })
      .catch((e: any) => {
        if (e.err.errorCode === 3008) {
          toast('Office name alredy exists.', {
            type: 'error',
            icon: true,
          });
          setValue('officeName', '', { shouldDirty: false });
        }
      });
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <Box
        component='form'
        width={'100%'}
        onSubmit={handleSubmit((formData) => onSubmitForm(formData))}
      >
        <IconButton
          aria-label='close'
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography
                variant='h2'
                sx={{ borderBottom: '1px solid #EAECF0' }}
                textAlign={'center'}
                pb={2}
              >
                {props.editData !== null ? 'Edit Practice' : 'Add Practice'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <InputLabel htmlFor='officeName'>Practice/Organization Name*</InputLabel>
              <TextField
                id='officeName'
                autoComplete='officeName'
                placeholder='Enter organization name'
                fullWidth={true}
                {...register('officeName')}
                error={!!errors.officeName}
                helperText={errors.officeName ? errors.officeName?.message : ''}
                inputProps={{
                  maxLength: 255,
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InputLabel htmlFor='officeType'>Type*</InputLabel>
              <TextField
                id='officeType'
                variant='outlined'
                fullWidth
                select
                {...register('officeType')}
                error={!!errors.officeType}
                value={watch('officeType') ? watch('officeType') : ''}
              >
                {OrganizationTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.name}
                  </MenuItem>
                ))}
              </TextField>
              {
                <Typography color='#d32f2f' fontSize='0.75rem'>
                  {errors.officeType?.message}
                </Typography>
              }
            </Grid>

            <Grid item xs={12} md={6}>
              <InputLabel htmlFor='officePhoneNumber'>Phone Number*</InputLabel>
              <ReactPhoneInput
                component={TextField}
                label=''
                onlyCountries={['us']}
                placeholder='+1 (555) 000-0000'
                country={'us'}
                masks={{ us: '(...) ...-....' }}
                autoFormat={true}
                disableDropdown={true}
                defaultErrorMessage={'Please enter a phone number'}
                {...register('officePhoneNumber')}
                inputProps={{
                  helperText: errors.officePhoneNumber ? errors.officePhoneNumber?.message : '',
                  error: !!errors.officePhoneNumber,
                }}
                countryCodeEditable={false}
                onChange={(e) => {
                  setValue('officePhoneNumber', e, { shouldValidate: true, shouldDirty: true });
                }}
                value={watch('officePhoneNumber') ? watch('officePhoneNumber') : null}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InputLabel htmlFor='officeFaxNumber*'>Fax Number</InputLabel>
              <ReactPhoneInput
                component={TextField}
                label=''
                onlyCountries={['us']}
                placeholder='+1 (555) 000-0000'
                country={'us'}
                masks={{ us: '(...) ...-....' }}
                autoFormat={true}
                disableDropdown={true}
                defaultErrorMessage={'Please enter a fax number'}
                {...register('officeFaxNumber')}
                inputProps={{
                  helperText: errors?.officeFaxNumber ? errors.officeFaxNumber?.message : '',
                  error: !!errors.officeFaxNumber,
                }}
                countryCodeEditable={false}
                onChange={(e) => {
                  setValue('officeFaxNumber', e, { shouldValidate: true, shouldDirty: true });
                }}
                value={watch('officeFaxNumber') ? watch('officeFaxNumber') : ''}
                onBlur={() => {
                  if (watch('officeFaxNumber') === '+1' || watch('officeFaxNumber') === null)
                    clearErrors('officeFaxNumber');
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InputLabel htmlFor='businessEmail'>Business Email</InputLabel>
              <TextField
                id='businessEmail'
                autoComplete='businessEmail'
                placeholder='Enter business email address'
                fullWidth
                {...register('businessEmail')}
                error={!!errors.businessEmail}
                helperText={errors.businessEmail ? errors.businessEmail?.message : ''}
                inputProps={{
                  maxLength: 255,
                }}
                value={watch('businessEmail') ? watch('businessEmail') : null}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputLabel htmlFor='websiteUrl'>Website URL</InputLabel>
              <TextField
                id='websiteUrl'
                variant='outlined'
                fullWidth
                {...register('websiteUrl')}
                error={!!errors.websiteUrl}
                helperText={errors.websiteUrl ? errors.websiteUrl?.message : ''}
                value={watch('websiteUrl') ? watch('websiteUrl') : null}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputLabel htmlFor='region'>Region*</InputLabel>
              <TextField
                id='region'
                variant='outlined'
                fullWidth
                select
                {...register('region')}
                error={!!errors.region}
                value={watch('region') ? watch('region') : ''}
              >
                {Regions.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.name}
                  </MenuItem>
                ))}
              </TextField>
              {
                <Typography color='#d32f2f' fontSize='0.75rem'>
                  {errors.region?.message}
                </Typography>
              }
            </Grid>
            <Grid item xs={12} md={6}>
              <InputLabel htmlFor='status'>Status*</InputLabel>
              <TextField
                id='status'
                variant='outlined'
                fullWidth
                select
                {...register('status')}
                error={!!errors.status}
                value={watch('status') ? watch('status') : ''}
              >
                {OrganizationStatus.map((status) => (
                  <MenuItem key={status.name} value={status.value}>
                    {status.value}
                  </MenuItem>
                ))}
              </TextField>
              {
                <Typography color='#d32f2f' fontSize='0.75rem'>
                  {errors.status?.message}
                </Typography>
              }
            </Grid>
            <Grid item xs={12} md={12}>
              <InputLabel htmlFor='Street Address'>Street Address*</InputLabel>
              <TextField
                id='address'
                fullWidth
                multiline
                rows={2}
                placeholder='Enter Street Address Here...'
                variant='filled'
                inputProps={{
                  maxLength: 255,
                }}
                {...register('address')}
                error={!!errors.address}
                helperText={errors.address ? errors.address?.message : ''}
                value={watch('address') ? watch('address') : ''}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputLabel htmlFor='city'>City*</InputLabel>
              <TextField
                id='city'
                variant='outlined'
                fullWidth
                {...register('city')}
                error={!!errors.city}
                value={watch('city') ? watch('city') : ''}
              ></TextField>
              {
                <Typography color='#d32f2f' fontSize='0.75rem'>
                  {errors.city?.message}
                </Typography>
              }
            </Grid>
            <Grid item xs={12} md={6}>
              <InputLabel htmlFor='state'>State*</InputLabel>
              <TextField
                id='state'
                variant='outlined'
                fullWidth
                select
                {...register('state')}
                error={!!errors.state}
                value={watch('state') ? watch('state') : ''}
              >
                {USStates.map((state) => (
                  <MenuItem key={state.value} value={state.value}>
                    {state.name}
                  </MenuItem>
                ))}
              </TextField>
              {
                <Typography color='#d32f2f' fontSize='0.75rem'>
                  {errors.state?.message}
                </Typography>
              }
            </Grid>

            <Grid item xs={12} md={6}>
              <InputLabel htmlFor='zipcode'>ZIP Code*</InputLabel>
              <TextField
                id='zipcode'
                variant='outlined'
                fullWidth
                {...register('zipcode')}
                error={!!errors.zipcode}
                value={watch('zipcode') ? watch('zipcode') : ''}
                inputProps={{ maxLength: 5 }}
              ></TextField>
              {
                <Typography color='#d32f2f' fontSize='0.75rem'>
                  {errors.zipcode?.message}
                </Typography>
              }
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant='outlined'
            onClick={() => {
              handleClose();
            }}
          >
            Cancel
          </Button>
          <Button variant='contained' type='submit' disabled={disableSaveButton}>
            Save
          </Button>
        </DialogActions>
      </Box>
    </StyledDialog>
  );
};

export default UpdateOffice;
